import classNames from "classnames";
import Input from "components/DesignSystem/Input/Input";
import { Button } from "components/DesignSystem/Button/Button";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { FormikForm } from "components/FormikForm/FormikForm";
import { IncorporationFormSource, ACTOR_TYPE } from "constants/incorporation";
import { useToast } from "hooks/useToast";
import { debounce } from "utils/debouncing";
import { Stage } from "store/apis/incorporationFormBuilder";
import { BackendError } from "types/utils/error";
import { useMemo } from "react";
import { isStageComplete } from "util/incorporationFormProgress";
import { IncorporationStepCard } from "./IncorporationStepCard";
import { BareRichTextEditor } from "components/DesignSystem/RichTextEditor/BareRichTextEditor";
import { RichTextView } from "components/DesignSystem/RichTextEditor/RichTextView";
import { IncorporationLockedFieldTooltip } from "./IncorporationLockedFieldTooltip";
import {
  useCreateFormTreeNodeMutation,
  useUpdateFormTreeNodeMutation,
} from "./useFormTreeNodeMutations";
import { Padlock10 } from "components/icons/Padlock10";
import { useActiveStepInView } from "./useActiveStepInView";

const NEW_STEP_DEFAULT_TITLE = "Untitled step";
const STAGE_NAME_PLACEHOLDER = "Enter stage name";
const STAGE_DESCRIPTION_PLACEHOLDER =
  "Enter details like who is handling this stage and what is it about.";

export const IncorporationStageDetail = ({
  stage,
  source,
  treeId,
  isEditable,
  isAnswerable = false,
  isLocked = false,
  isPublished = false,
  groupId,
}: {
  stage: Stage;
  source: IncorporationFormSource;
  treeId: string;
  isEditable: boolean;
  isAnswerable?: boolean;
  isLocked?: boolean;
  isPublished?: boolean;
  groupId?: string;
}) => {
  const stepIds = useMemo(
    () => (isLocked ? [] : stage.steps.map((step) => step.uuid)),
    [isLocked, stage.steps]
  );
  const { activeStepId, setStepRef } = useActiveStepInView(stepIds);
  const { alertToast } = useToast();
  const [updateStage] = useUpdateFormTreeNodeMutation({ source });
  const [addStep, { isLoading: isAddingStep }] = useCreateFormTreeNodeMutation({
    source,
  });

  const onActionError = (err: unknown) => {
    alertToast({
      message:
        (err as BackendError)?.data?.error?.message ?? "Something went wrong",
    });
  };

  const saveTitle = async (title: string) => {
    try {
      await updateStage({
        treeId,
        body: { stage: { node_id: stage.uuid, title } },
      }).unwrap();
    } catch (err) {
      onActionError(err);
    }
  };

  const saveDescription = async (description: string) => {
    try {
      await updateStage({
        treeId,
        body: { stage: { node_id: stage.uuid, description } },
      }).unwrap();
    } catch (err) {
      onActionError(err);
    }
  };

  const debouncedSaveTitle = useMemo(() => debounce(saveTitle), [stage.uuid]);
  const debouncedSaveDescription = useMemo(
    () => debounce(saveDescription),
    [stage.uuid]
  );

  const isStageLocked = isPublished && isStageComplete(stage);

  const onAddNewStep = async () => {
    try {
      await addStep({
        treeId,
        body: {
          step: {
            parent_id: stage.uuid,
            title: NEW_STEP_DEFAULT_TITLE,
            actor_type: ACTOR_TYPE.CUSTOMER,
          },
        },
      }).unwrap();
    } catch (err) {
      onActionError(err);
    }
  };

  if (!isEditable) {
    return (
      <div className="t-flex t-flex-1 t-flex-col t-gap-10 t-overflow-y-auto t-px-3 t-pb-6 t-pt-12 sm:t-gap-6 sm:t-px-0 sm:t-pt-6">
        <div className="t-flex t-items-start t-justify-between t-gap-2">
          <div className="t-flex t-flex-col t-gap-1">
            <span className="t-text-title-h3 t-text-text-60">
              {stage.title || "Untitled stage"}
            </span>
            {stage.description && (
              <RichTextView
                hasLegacyMarkdown
                value={stage.description}
                className="t-text-body-sm t-text-text-30"
              />
            )}
          </div>
        </div>
        {isLocked && (
          <Banner
            type="info"
            icon={
              <span className="t-flex t-shrink-0 t-self-start t-text-blue-80">
                <Padlock10 />
              </span>
            }
            title="This stage is currently unavailable."
            description="It will be unlocked automatically once all required preceding stages have been completed."
          />
        )}
        <div
          className={classNames("t-flex t-flex-col t-gap-10 sm:t-gap-4", {
            "t-opacity-60": isLocked,
          })}
        >
          {stage.steps.map((step, index) => (
            <IncorporationStepCard
              key={step.uuid}
              step={step}
              order={index + 1}
              source={source}
              treeId={treeId}
              stageId={stage.uuid}
              isEditable={false}
              isAnswerable={isAnswerable && !isLocked}
              isActive={!isLocked && step.uuid === activeStepId}
              stepRef={setStepRef(step.uuid)}
              groupId={groupId}
              isPublished={isPublished}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="t-flex t-flex-1 t-flex-col t-gap-12 t-overflow-y-auto t-pb-6 t-pt-10 sm:t-gap-4 sm:t-pt-4">
      <IncorporationLockedFieldTooltip isLocked={isStageLocked}>
        <FormikForm
          initialValues={{ title: stage.title }}
          onSubmit={() => {}}
          className="t-flex t-flex-col t-gap-5"
        >
          <span className="t-text-subtext-lg t-text-text-60">
            Stage Details
          </span>
          <div className="t-flex t-flex-col t-gap-3">
            <Input
              withForm
              name="title"
              label="Stage name"
              placeholder={STAGE_NAME_PLACEHOLDER}
              required
              disabled={isStageLocked}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                debouncedSaveTitle(e.target.value)
              }
            />
            <div className="t-flex t-flex-col t-gap-1">
              <span className="t-text-body-sm t-text-text-60">
                Stage description
              </span>
              <BareRichTextEditor
                hasLegacyMarkdown
                defaultValue={stage.description}
                placeholder={STAGE_DESCRIPTION_PLACEHOLDER}
                onChange={debouncedSaveDescription}
                isDisabled={isStageLocked}
              />
            </div>
          </div>
        </FormikForm>
      </IncorporationLockedFieldTooltip>

      {stage.steps.map((step, index) => (
        <IncorporationStepCard
          key={step.uuid}
          step={step}
          order={index + 1}
          source={source}
          treeId={treeId}
          stageId={stage.uuid}
          isEditable
          isActive={false}
          isPublished={isPublished}
          stepRef={setStepRef(step.uuid)}
        />
      ))}

      <IncorporationLockedFieldTooltip isLocked={isStageLocked}>
        <Button
          type="button"
          customType="secondary"
          block
          isLoading={isAddingStep}
          disabled={isAddingStep || isStageLocked}
          onClick={onAddNewStep}
          className="!t-border-dashed"
        >
          + Add new step
        </Button>
      </IncorporationLockedFieldTooltip>
    </div>
  );
};
